import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { CARDS_REQUIREMENTS_BY_SPORT } from '@sorare/core/src/lib/referral';

import { Gameplay } from '@sorare/us-sports/src/components/competitionDetails/Gameplay';
import { Layout } from '@sorare/us-sports/src/components/competitionDetails/Layout';

import { BudgetRequirements } from 'components/competitionDetails/BudgetRequirements';
import { ScoringStrategy } from 'components/competitionDetails/ScoringStrategy';

import { CompetitionDetailsGameplay_NBALeaderboard } from './__generated__/index.graphql';

const messages = defineMessages({
  gameplay: {
    id: 'CompetitionDetails.Gameplay.Label',
    defaultMessage: 'Gameplay',
  },
});

type Props = {
  leaderboard?: CompetitionDetailsGameplay_NBALeaderboard | null;
  fullPage?: boolean;
};

const Content = ({ fullPage, leaderboard }: Props) => {
  const { requirements } = leaderboard || {};
  return (
    <Gameplay
      leaderboard={leaderboard}
      nbCards={CARDS_REQUIREMENTS_BY_SPORT[Sport.NBA]}
      BudgetRequirements={<BudgetRequirements requirements={requirements} />}
      ScoringStrategy={<ScoringStrategy leaderboard={leaderboard} />}
      fullPage={fullPage}
    />
  );
};

export const CompetitionDetailsGameplay = ({ fullPage, ...rest }: Props) => {
  if (!fullPage) return <Content {...rest} fullPage={fullPage} />;
  return (
    <Layout backLink={<FormattedMessage {...messages.gameplay} />}>
      <Content {...rest} fullPage={fullPage} />
    </Layout>
  );
};

CompetitionDetailsGameplay.fragments = {
  NBALeaderboard: gql`
    fragment CompetitionDetailsGameplay_NBALeaderboard on NBALeaderboard {
      slug
      requirements {
        ...CompetitionDetailsGameplayBudgetRequirements_NBALeaderboardRequirements
      }
      ...CompetitionDetailsGameplay_LeaderboardInterface
      ...CompetitionDetailsGameplayScoringStrategy_NBALeaderboard
    }
    ${BudgetRequirements.fragments.NBALeaderboardRequirements}
    ${Gameplay.fragments.LeaderboardInterface}
    ${ScoringStrategy.fragments.NBALeaderboard}
  ` as TypedDocumentNode<CompetitionDetailsGameplay_NBALeaderboard>,
};
